import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { DrawerPosition, DrawerProps, DrawerState } from './types';

// ─── Constants ────────────────────────────────────────────────────────────────

const OPEN_DURATION  = 300; // ms — slide-in
const CLOSE_DURATION = 250; // ms — slide-out

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),' +
  'select:not([disabled]),[tabindex]:not([tabindex="-1"])';

// ─── Position class maps ──────────────────────────────────────────────────────

// Panel placement
const PANEL_PLACEMENT: Record<DrawerPosition, string> = {
  right:  'right-0 top-0 bottom-0',
  left:   'left-0 top-0 bottom-0',
  bottom: 'bottom-0 left-0 right-0',
};

// Translate-out (closed/closing) → translate-in (opening/open)
const PANEL_TRANSLATE_HIDDEN: Record<DrawerPosition, string> = {
  right:  'translate-x-full',
  left:   '-translate-x-full',
  bottom: 'translate-y-full',
};

const PANEL_TRANSLATE_VISIBLE = 'translate-x-0 translate-y-0';

// ─── Focus trap helper ────────────────────────────────────────────────────────

function trapFocus(panelEl: HTMLElement, event: KeyboardEvent) {
  const focusable = Array.from(panelEl.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => el.offsetParent !== null,
  );
  if (focusable.length === 0) return;
  const first = focusable[0];
  const last  = focusable[focusable.length - 1];
  if (event.shiftKey) {
    if (document.activeElement === first) {
      event.preventDefault();
      last.focus();
    }
  } else {
    if (document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

// ─── DragHandle (bottom drawer only) ─────────────────────────────────────────

function DragHandle() {
  return (
    <div className="flex justify-center pt-2 pb-1 shrink-0" aria-hidden="true">
      <span className="h-1 w-10 rounded-full bg-neutral-300 dark:bg-neutral-600" />
    </div>
  );
}

// ─── Drawer ───────────────────────────────────────────────────────────────────

export function Drawer({
  open,
  onClose,
  position = 'right',
  width = '360px',
  height = '50vh',
  title,
  children,
  showBackdrop = true,
}: DrawerProps) {
  const labelId  = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<Element | null>(null);
  const [state, setState] = useState<DrawerState>('closed');

  // Lifecycle: open → opening → open, open → closing → closed
  useEffect(() => {
    if (open) {
      triggerRef.current = document.activeElement;
      setState('opening');
      const t = setTimeout(() => setState('open'), OPEN_DURATION);
      return () => clearTimeout(t);
    } else {
      if (state === 'closed') return;
      setState('closing');
      const t = setTimeout(() => {
        setState('closed');
        if (triggerRef.current && 'focus' in triggerRef.current) {
          (triggerRef.current as HTMLElement).focus();
        }
      }, CLOSE_DURATION);
      return () => clearTimeout(t);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-focus first focusable element when fully open
  useEffect(() => {
    if (state === 'open' && panelRef.current) {
      const el = panelRef.current.querySelector<HTMLElement>(FOCUSABLE);
      el ? el.focus() : panelRef.current.focus();
    }
  }, [state]);

  // Keyboard: Escape → close, Tab → trap focus
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Tab' && panelRef.current) {
        trapFocus(panelRef.current, e.nativeEvent);
      }
    },
    [onClose],
  );

  if (state === 'closed') return null;

  const isVisible = state === 'open' || state === 'opening';

  // Backdrop opacity
  const backdropOpacity = isVisible ? 'opacity-100' : 'opacity-0';

  // Panel transform
  const panelTransform = isVisible ? PANEL_TRANSLATE_VISIBLE : PANEL_TRANSLATE_HIDDEN[position];

  // Transition duration class for panel
  const panelDuration = state === 'closing' ? 'duration-[250ms]' : 'duration-[300ms]';

  // Panel inline sizing (left/right use width; bottom uses height + full width)
  const panelStyle: React.CSSProperties =
    position === 'bottom'
      ? { height }
      : { width, maxWidth: '100vw' };

  // Panel shape: no rounding on side drawers; top corners for bottom drawer
  const panelRadius = position === 'bottom' ? 'rounded-t-xl' : '';

  return (
    <div
      className="fixed inset-0 z-[40] overflow-hidden"
      onKeyDown={handleKeyDown}
      aria-hidden={!open}
    >
      {/* Backdrop */}
      {showBackdrop && (
        <div
          className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ${backdropOpacity}`}
          aria-hidden="true"
          onClick={onClose}
        />
      )}

      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        aria-labelledby={title ? labelId : undefined}
        tabIndex={-1}
        style={panelStyle}
        className={[
          'absolute flex flex-col bg-white dark:bg-neutral-900 shadow-xl outline-none',
          'z-[50]',
          PANEL_PLACEMENT[position],
          panelRadius,
          'transition-transform ease-in-out',
          panelDuration,
          panelTransform,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {/* Drag handle — bottom drawer only */}
        {position === 'bottom' && <DragHandle />}

        {/* Header */}
        {title && (
          <div className="flex items-center justify-between gap-4 border-b border-neutral-200 dark:border-neutral-700 px-4 py-3 shrink-0">
            <h2
              id={labelId}
              className="text-base font-semibold text-neutral-900 dark:text-neutral-100 truncate"
            >
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className={[
                'shrink-0 flex items-center justify-center rounded p-1',
                'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700',
                'dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-200',
                'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
              ].join(' ')}
            >
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}
